import React from "react";
import style from "@/styles/pages/Address/Address.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import ManualAddress from "@/components/Address/ManualAddress";
import { useDispatch, useSelector } from "react-redux";
import {
  setStreetNumber,
  setStreetName,
  setUnitNumber,
  setCity,
  setProvince,
  setPostalCode,
  setCountry,
  selectHomeAddress,
  setMailingAddressSame,
  setStreetDirection,
  setStreetType,
  setUnitType,
  setAddressId,
} from "@/slices/homeAddressSlice";
import { setAddressComplete, setCoapplicantComplete, setCredentialsComplete } from "@/slices/progressSlice";
import { useNavigate } from "react-router-dom";
import { selectApplication, setApplicationProgress } from "@/slices/applicationSlice";
import { setResidenceMonths, setResidenceYears, setResidentStatus } from "@/slices/applicantSlice";
import { RootState } from "@/store/dtm";
import { addressInfo, clearPreviousAddressState } from "@/slices/PreviousAddressSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { AddressDto, ApplicationDto, AssetDto, DealAssetsDto, DealExpensesDto, FinancialAssetDto, LifeInsuranceDto, PropertyExpenseDto, RealEstateDto, VehicleDto } from "@/lib/DTMWebAPIClient.generated";
import { createApiClient } from "@/lib/Requests";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { AnimatePresence, motion } from "framer-motion";
import 'animate.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";
import { addAsset, assetInfo, removeAsset, setAssetInfo } from "@/slices/assetSlice";
import { expenseInfo, addExpense, setExpenseInfo, removeExpense } from "@/slices/expenseSlice";
import dayjs from "dayjs";
import { applicationCategory } from "@/types/applicationType";
import { setCachedApplicant } from "@/slices/sessionDataSlice";
import { selectCoApplicant } from "@/slices/coApplicantSlice";

export default function Address() {
  const navigate = useNavigate();
  const [manualAddressShown, setmanualAddressShown] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [addressSelectionError, setAddressSelectionError] = useState(false);

  const [residentStatusText, setResidentStatusText] = useState("");
  const [residenceYearsText, setResidenceYearsText] = useState("");
  const [residenceMonthsText, setResidenceMonthsText] = useState("");
  const [displayError, setDisplayError] = useState(false);
  const [focusErrorType, setFocusErrorType] = useState(0);
  const [highlightError, setHighlightError] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [removeOwnRealEstateAsset, setRemoveOwnRealEstateAsset] = useState(false);

  const dispatch = useDispatch();
  const homeAddress = useSelector(selectHomeAddress);
  const application = useSelector(selectApplication).application;
  const assets = useSelector((state: RootState) => state.assets.assets);
  const expenses = useSelector((state: RootState) => state.expenses.expenses);
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const coapplicants = useSelector(selectCoApplicant).coapplicants;
  const employment = useSelector(
    (state: RootState) => state.employment.employment
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const [currentAddressInfo, setCurrentAddressInfo] = useState<addressInfo>({
    id: 0,
    displayId: "",
    streetNumber: "",
    streetName: "",
    streetType: "",
    streetDirection: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
    unitType: "",
    unitNumber: "",
    manualAddressEntered: true,
    yearsLivedOn: applicant.residenceYears,
    monthsLivedOn: applicant.residenceMonths
  });

  const methods = useForm({
    defaultValues: {
      autoAddress: "",
      autoMailAddress: "",
      streetNumber: homeAddress.address.streetName,
      streetName: homeAddress.address.streetNumber,
      streetType: homeAddress.address.streetType,
      streetDirection: homeAddress.address.streetDirection,
      unitType: homeAddress.address.unitType,
      unitNumber: homeAddress.address.unitNumber,
      cityName: homeAddress.address.city,
      provinceName: homeAddress.address.province,
      postalCode: homeAddress.address.postalCode,
      country: homeAddress.address.country,
      isMailingAddressSame: true,
      residentStatus: "",
      residenceYears: "",
      residenceMonths: ""
    },
  });

  const [residentStatus, residenceYears, residenceMonths] = methods.watch(["residentStatus", "residenceYears", "residenceMonths"])

  const fields = [
    {
      element: "search",
      field: "",
      mode: window.pca?.fieldMode.SEARCH,
    },
    {
      element: "span-street-address",
      field: "Line1",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-street-address2",
      field: "Line2",
      mode: window.pca?.fieldMode?.POPULATE,
    },
    { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
    {
      element: "span-state",
      field: "ProvinceCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-postcode",
      field: "PostalCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-country",
      field: "CountryName",
      mode: window.pca?.fieldMode.POPULATE,
    },
  ];
  const options = {
    key: "XD95-YY77-BP99-EM55",
  };

  const toggleManualAddress = () => {
    setmanualAddressShown(!manualAddressShown);
    if (!manualAddressShown) {
      // dispatch(clearAddressState());
      formatAddress();
      setShowAddress(false);
    } else {
      if (currentAddressInfo != undefined && currentAddressInfo.streetNumber.trim() != "") {
        methods.setValue("autoAddress", currentAddressInfo.streetNumber + " " + currentAddressInfo.streetName + " " + currentAddressInfo.streetType
          + (homeAddress.address.streetDirection.trim() != "" ? (" " + homeAddress.address.streetDirection) : ""));
        setShowAddress(true);
      }
    }
  };

  const residentStatusUpdated = (newStatus: any, initialFetch: boolean) => {
    if (newStatus == "own") {
      if (!manualAddressShown) {
        formatAddress();
      }
      let assetId = (assets.length + 1).toString();

      let newAssetInfo: assetInfo = {
        displayId: assetId,
        id: 0,
        visible: true,
        assetCategory: "realestate",
        assetType: "",
        origin: "",
        value: 0,
        valueText: "",
        description: "",
        downPaymentValue: 0,
        downPaymentValueText: "",
        applicants: [applicant.id],
        details: {
          nonRegistered: undefined,
          registered: undefined,
          realEstate: {
            occupancyType: "primary",
            propertyType: "",
            rentalComponent: "",
            numOfProperties: "",
            ownershipPercentage: "",
            address: {
              displayId: assetId,
              streetNumber: initialFetch ? homeAddress.address.streetNumber : methods.getValues("streetNumber"),
              streetName: initialFetch ? homeAddress.address.streetName : methods.getValues("streetName"),
              streetType: initialFetch ? homeAddress.address.streetType : methods.getValues("streetType"),
              streetDirection: initialFetch ? homeAddress.address.streetDirection : methods.getValues("streetDirection"),
              unitType: initialFetch ? homeAddress.address.unitType : methods.getValues("unitType"),
              unitNumber: initialFetch ? homeAddress.address.unitNumber : methods.getValues("unitNumber"),
              city: initialFetch ? homeAddress.address.city : methods.getValues("cityName"),
              province: initialFetch ? homeAddress.address.province : methods.getValues("provinceName"),
              postalCode: initialFetch ? homeAddress.address.postalCode : methods.getValues("postalCode"),
              country: initialFetch ? homeAddress.address.country : methods.getValues("country")
            },
            originalPurchasePrice: 0,
            originalPurchasePriceText: "",
            originalPurchaseDate: "",
            propertyTax: 0,
            propertyTaxText: "",
            heatingCost: 0,
            heatingCostText: "",
            maintenanceFee: 0,
            maintenanceFeeText: "",
            lot: "",
            block: "",
            plan: "",
            section: "",
            futureUsage: "",
            hasPropertyBeenSold: "",
            soldDate: "",
            rentalIncome: 0,
            rentalIncomeText: "",
            manualAddressEntered: false,
            isCurrentResidence: true
          },
          vehicle: undefined,
          lifeInsurance: undefined
        }
      };
      dispatch(addAsset(newAssetInfo));
      setRemoveOwnRealEstateAsset(false);
    } else {
      //Remove own real estate asset if it exists
      assets.forEach(a => {
        if (a.assetCategory == "realestate" && a.details.realEstate && a.details.realEstate.isCurrentResidence) {
          dispatch(removeAsset(a.displayId));

          //Remove expense linked to this asset
          expenses.forEach(exp => {
            if (exp.assetId == a.id) {
              dispatch(removeExpense(exp.id));
            }
          });
        }
        setRemoveOwnRealEstateAsset(true);
      });
    }
  }

  const residentStatusChangeHandler = (e: any) => {
    setResidentStatusText(e.target.value);
    methods.setValue("residentStatus", e.target.value);
    methods.clearErrors("residentStatus");

    if (application.applicationCategory == applicationCategory.Buy) {
      residentStatusUpdated(e.target.value, false);
    }
  }

  const residenceYearsChangeHandler = (e: any) => {
    if (e.target.value.charAt(0) == "0") {
      e.target.value = e.target.value.slice(1, 2);
    } else {
      e.target.value = e.target.value.slice(0, 2);
    }
    setResidenceYearsText(e.target.value);
    methods.clearErrors("residenceYears");
    methods.setValue("residenceYears", e.target.value);

    if (methods.getValues("residenceMonths").trim() == "") {
      methods.setValue("residenceMonths", "0");
    }

    if (methods.formState.errors == undefined || Object.keys(methods.formState.errors).length == 0) {
      setDisplayError(false);
    }
  }

  const residenceMonthsChangeHandler = (e: any) => {
    if (e.target.value.charAt(0) == "0") {
      e.target.value = e.target.value.slice(1, 2);
    } else {
      e.target.value = e.target.value.slice(0, 2);
    }
    if (+e.target.value > 11) {
      e.target.value = "11"
    }
    setResidenceMonthsText(e.target.value);
    methods.clearErrors("residenceMonths");
    methods.setValue("residenceMonths", e.target.value);

    if (methods.formState.errors == undefined || Object.keys(methods.formState.errors).length == 0) {
      setDisplayError(false);
    }
  }

  useEffect(() => {
    document.title = "Where do you live?";

    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
    //*set profile section complete
    dispatch(setCredentialsComplete(true));
    dispatch(setCoapplicantComplete(true));
    dispatch(setCachedApplicant(applicant));

    const control = new window.pca.Address(fields, options);
    control.listen("populate", (address: any) => {
      setAddressSelected(true);
      methods.setValue("autoAddress", address.Line1);
      let streetInfo = address.Line1.split(" ");
      let streetName = "";
      let streetDirection = "";
      let streetType = "";
      let streetComponents: string[] = address.Street.split(" ");
      let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

      //Check if street direction and type exists in the street name
      if (streetDirections.includes(lastStreetComponent)) {
        streetDirection = lastStreetComponent;
        let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
        if (streetTypes.includes(secondLastStreetComponent)) {
          streetType = secondLastStreetComponent;
        }
        streetComponents.forEach((value, index) => {
          if (index < streetComponents.length - 2) {
            streetName += value + " "
          }
        });
      } else if (streetTypes.includes(lastStreetComponent)) {
        streetType = lastStreetComponent;
        streetComponents.forEach((value, index) => {
          if (index != streetComponents.length - 1) {
            streetName += value + " "
          }
        });
      } else {
        streetComponents.forEach((value, index) => {
          console.log(value, index);
          streetName += value + " "
        });
      }
      streetName = streetName.trim();

      //Check if unit number is embeded in the street number
      let streetNumber = "";
      let streetNumberComponents = streetInfo[0].split("-");
      if (streetNumberComponents.length > 1) {
        streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
      } else {
        streetNumber = streetInfo[0];
      }

      let addressInfo: addressInfo = {
        id: 0,
        displayId: "",
        streetNumber: streetNumber,
        streetName: streetName,
        streetType: streetType,
        streetDirection: streetDirection,
        city: address.City,
        province: address.ProvinceCode,
        country: address.CountryName,
        postalCode: address.PostalCode,
        unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
        unitNumber: address.SubBuilding != undefined ? address.SubBuilding : "",
        manualAddressEntered: false,
        yearsLivedOn: applicant.residenceYears,
        monthsLivedOn: applicant.residenceMonths
      }
      setCurrentAddressInfo(addressInfo);

      if (address.SubBuilding != undefined && address.SubBuilding != "") {
        methods.setValue("unitNumber", addressInfo.unitNumber);
        methods.setValue("unitType", "unit");
        methods.register("unitType", {
          required: "Select a unit type",
          pattern: {
            value: /^[ #a-zA-Z0-9._-]+$/,
            message: "Select a unit type",
          },
          validate: {
            unitType: (c) => {
              if (c.trim() != "") {
                return true
              } else {
                return "Select a unit type";
              }
            },
          },
        });
      }

      methods.setValue("streetNumber", addressInfo.streetNumber);
      methods.setValue("streetName", addressInfo.streetName);
      methods.setValue("streetType", addressInfo.streetType);
      methods.setValue("streetDirection", addressInfo.streetDirection);
      methods.setValue("cityName", addressInfo.city);
      methods.setValue("provinceName", addressInfo.province);
      methods.setValue("postalCode", addressInfo.postalCode);
      methods.setValue("country", addressInfo.country);
      methods.clearErrors();

      //If Street Type is not in the master list, toggle manual address entry
      if (!streetTypes.includes(addressInfo.streetType)) {
        toggleManualAddress();
      }

      if (application.applicationCategory == applicationCategory.Buy) {
        updateRealEstateAddress();
      }
    });

    client.apiApplicationsFetch().then((result) => {
      result.applicant = applicant;
      result.coapplicants = coapplicants;
      result.employment = employment;
      result.application = application;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });

    methods.setValue("residentStatus", applicant.residentStatus);
    setResidentStatusText(applicant.residentStatus);
    methods.setValue("residenceYears", applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "0");
    setResidenceYearsText(applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "0")
    methods.setValue("residenceMonths", applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "0");
    setResidenceMonthsText(applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "0")

    if (homeAddress != undefined && homeAddress.address != undefined && homeAddress.address.city != "" && !manualAddressShown) {
      try {
        const span = document.getElementById("span-street-address");
        if (span != null) {
          span.innerText = (homeAddress.address.unitNumber != "" ? homeAddress.address.unitNumber + "-" : "") +
            homeAddress.address.streetNumber + " " + homeAddress.address.streetName + " "
            + homeAddress.address.streetType + (homeAddress.address.streetDirection.trim() != "" ? (" " + homeAddress.address.streetDirection) : "");
          methods.setValue("autoAddress", span.innerText);
        }
        const span2 = document.getElementById("span-city");
        if (span2 != null) {
          span2.innerText = homeAddress.address.city;

        }
        const span3 = document.getElementById("span-state");
        if (span3 != null) {
          span3.innerText = homeAddress.address.province != undefined ? homeAddress.address.province : "";

        }
        const span4 = document.getElementById("span-postcode");
        if (span4 != null) {
          span4.innerText = homeAddress.address.postalCode != undefined ? homeAddress.address.postalCode : "";

        }
        const span5 = document.getElementById("span-country");
        if (span5 != null) {
          span5.innerText = homeAddress.address.country;
        }
      } catch (e) { }

      if (!applicant.isProspect) {
        setAddressSelected(true);
        toggleManualAddress();
        setShowAddress(true);
      }

      let addressInfo: addressInfo = {
        id: 0,
        displayId: "",
        streetNumber: homeAddress.address.streetNumber,
        streetName: homeAddress.address.streetName,
        streetType: homeAddress.address.streetType,
        streetDirection: homeAddress.address.streetDirection,
        city: homeAddress.address.city,
        province: homeAddress.address.province,
        country: homeAddress.address.country,
        postalCode: homeAddress.address.postalCode,
        unitType: homeAddress.address.unitType,
        unitNumber: homeAddress.address.unitNumber,
        manualAddressEntered: false,
        yearsLivedOn: applicant.residenceYears,
        monthsLivedOn: applicant.residenceMonths
      }
      setCurrentAddressInfo(addressInfo);
      setShowAddress(true);
      setAddressSelectionError(false);
    }

    if (!applicant.isProspect) {
      toggleManualAddress();
      let ownRealEstateExists = false;
      assets.forEach(asset => {
        if (asset.details?.realEstate?.isCurrentResidence) {
          ownRealEstateExists = true;
        }
      });

      if (applicant.residentStatus.toLowerCase() == "own" && !ownRealEstateExists) {
        residentStatusUpdated(applicant.residentStatus, true);
      }
    }
  }, []);

  useEffect(() => {
    if (!manualAddressShown) {
      methods.unregister([
        "streetNumber",
        "streetName",
        "unitNumber",
        "cityName",
        "provinceName",
        "postalCode",
        "country",
      ]);
      if (currentAddressInfo && currentAddressInfo.streetName.trim() == "") {
        methods.register("autoAddress", {
          required: "Enter your address",
        });
      }
    } else {
      //methods.setValue("autoAddress", "");
      if ((addressSelected && currentAddressInfo) || manualAddressShown) {
        methods.setValue("streetNumber", currentAddressInfo.streetNumber);
        methods.setValue("streetName", currentAddressInfo.streetName);
        methods.setValue("streetType", currentAddressInfo.streetType);
        methods.setValue("streetDirection", currentAddressInfo.streetDirection);
        methods.setValue("unitType", currentAddressInfo.unitType);
        methods.setValue("unitNumber", currentAddressInfo.unitNumber);
        methods.setValue("cityName", currentAddressInfo.city);
        methods.setValue("provinceName", currentAddressInfo.province);
        methods.setValue("postalCode", currentAddressInfo.postalCode);
        methods.setValue("country", currentAddressInfo.country);
      } else {
        methods.setValue("streetNumber", "");
        methods.setValue("streetName", "");
        methods.setValue("streetType", "");
        methods.setValue("streetDirection", "");
        methods.setValue("unitType", "");
        methods.setValue("unitNumber", "");
        methods.setValue("cityName", "");
        methods.setValue("provinceName", "");
        methods.setValue("postalCode", "");
        methods.setValue("country", "");
      }

      methods.unregister("autoAddress");
      methods.register("streetNumber", {
        required: "Enter your street number",
        pattern: {
          value: /^[ #a-zA-Z0-9._-]+$/,
          message: " Please enter a valid street number",
        },
      });
      methods.register("unitNumber", {
        required: methods.getValues("unitType") != undefined && methods.getValues("unitType").trim() != "" ? "Enter your unit number" : false,
        pattern: {
          value: /^[ #a-zA-Z0-9._-]+$/,
          message:
            " Please enter a valid unit number. Only letters, numbers and _-.# are accepted",
        },
      });
      methods.register("unitType", {
        required: methods.getValues("unitNumber") != undefined && methods.getValues("unitNumber").trim() != "" ? "Select a unit type" : false,
        pattern: {
          value: /^[ #a-zA-Z0-9._-]+$/,
          message: "Select a unit type",
        },
        validate: {
          unitType: (c) => {
            if (c.trim() != "" || methods.getValues("unitNumber") == undefined || methods.getValues("unitNumber").trim() == "") {
              return true
            } else {
              return "Select a unit type";
            }
          },
        },
      });
      methods.register("streetName", {
        required: "Enter your street name",
        pattern: {
          value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
          message: " Please enter a valid street name",
        },
      });
      methods.register("streetType", {
        required: "Select your street type"
      });
      methods.register("cityName", {
        required: "Enter your city name",
        pattern: {
          value: /^[ #a-zA-ZÀ-Ÿ0-9._'-]+$/,
          message: " Please enter a valid city name",
        },
      });
      if (currentAddressInfo && (currentAddressInfo.country === "Canada" || currentAddressInfo.country === "United States")) {
        methods.register("provinceName", {
          required: "Select your province name",
        });
        methods.register("postalCode", {
          required: "Enter your postal code",
        });
      }
      methods.register("country", {
        required: "Select your country name",
      });
      if (residenceYearsText == "0" && residenceMonthsText != "0") {
        methods.setValue("residenceYears", "0");
      }
      if (residenceMonthsText == "0" && residenceYearsText != "0") {
        methods.setValue("residenceMonths", "0");
      }
    }

    if (methods.formState.errors == undefined || Object.keys(methods.formState.errors).length == 0) {
      setDisplayError(false);
    }
    
  }, [manualAddressShown, methods, currentAddressInfo]);

  const formatAddress = () => {
    const span = document.getElementById("span-street-address");
    const span2 = document.getElementById("span-city");
    const span3 = document.getElementById("span-state");
    const span4 = document.getElementById("span-postcode");
    const span5 = document.getElementById("span-country");

    const streetAddress = span!.innerHTML.split(" ");
    const streetNumber = streetAddress.shift();

    methods.setValue("autoAddress", span!.innerHTML);
    methods.setValue("cityName", span2!.innerHTML);
    methods.setValue("provinceName", span3!.innerHTML);
    methods.setValue("postalCode", span4!.innerHTML);
    methods.setValue("country", span5!.innerHTML);
    methods.setValue("streetName", currentAddressInfo.streetName);
    methods.setValue("residentStatus", applicant.residentStatus);
    methods.setValue("residenceYears", applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "");
    methods.setValue("residenceMonths", applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "");


    if (streetNumber?.includes("-")) {
      const unit = streetNumber.split("-")[0];
      const number = streetNumber.split("-")[1];
      methods.setValue("unitNumber", unit!);
      methods.setValue("streetNumber", number!);
    } else {
      methods.setValue("unitNumber", "");
      methods.setValue("streetNumber", streetNumber!);
    }
  };

  const setHomeAddress = async (data: any) => {
    dispatch(setStreetNumber(methods.getValues("streetNumber")));
    dispatch(setStreetName(methods.getValues("streetName")));
    dispatch(setStreetType(methods.getValues("streetType")));
    dispatch(setStreetDirection(methods.getValues("streetDirection")));
    dispatch(setUnitType(methods.getValues("unitType") != undefined ? methods.getValues("unitType").trim() : ""));
    dispatch(setUnitNumber((methods.getValues("unitNumber") != undefined ? methods.getValues("unitNumber") : "")));
    dispatch(setCity(methods.getValues("cityName")));
    dispatch(setProvince(methods.getValues("provinceName")));
    dispatch(setPostalCode(methods.getValues("postalCode")));
    dispatch(setCountry(methods.getValues("country")));
    dispatch(setMailingAddressSame(methods.getValues("isMailingAddressSame")));

    //Update Address Info on Trellis Backend
    if (homeAddress.address.id != 0) {
      await client.apiApplicantsAddressPut(homeAddress.address.id, applicant.id, new AddressDto({
        id: homeAddress.address.id,
        streetNumber: methods.getValues("streetNumber"),
        streetName: methods.getValues("streetName"),
        streetType: methods.getValues("streetType"),
        streetDirection: methods.getValues("streetDirection"),
        unitType: methods.getValues("unitType"),
        unitNumber: methods.getValues("unitNumber"),
        city: methods.getValues("cityName"),
        region: methods.getValues("provinceName"),
        postalCode: methods.getValues("postalCode"),
        country: methods.getValues("country"),
        timeAtResidenceInMonths: (+residenceYears * 12) + +residenceMonths,
        residenceStatusType_Name: residentStatus,
        deliveryMode: undefined,
        modeNumber: undefined,
        deliveryMode2: undefined,
        modeNumber2: undefined,
        isMailing: false
      })).then((result) => {
        console.log("Trellis Address Updated");
      }).catch((error) => {
        console.log("Error updating trellis address");
      });
    } else {
      let newAddress = new AddressDto({
        id: 0,
        streetNumber: methods.getValues("streetNumber"),
        streetName: methods.getValues("streetName"),
        streetType: methods.getValues("streetType"),
        streetDirection: methods.getValues("streetDirection"),
        unitType: methods.getValues("unitType"),
        unitNumber: methods.getValues("unitNumber"),
        city: methods.getValues("cityName"),
        region: methods.getValues("provinceName"),
        postalCode: methods.getValues("postalCode"),
        country: methods.getValues("country"),
        timeAtResidenceInMonths: (+residenceYears * 12) + +residenceMonths,
        residenceStatusType_Name: residentStatus,
        deliveryMode: undefined,
        modeNumber: undefined,
        deliveryMode2: undefined,
        modeNumber2: undefined,
        isMailing: false
      });
      await client.apiApplicantsAddressesPost([newAddress]).then((result) => {
        console.log("Trellis Address Added");
        result.forEach((address) => {
          if (address.residenceStatusType_Name == data.residentStatus) {
            dispatch(setAddressId(address.id ? address.id : 0));
          }
        });
      }).catch((error) => {
        console.log("Error updating trellis address");
      });
    }
  };

  const onError = (errors: object) => {
    setClickInsightFormErrors(errors);
    setDisplayError(true);
    setHighlightError(0);
  }

  useEffect(() => {
    if (currentAddressInfo.streetNumber.trim() != "" && !manualAddressShown) {
      setShowAddress(true);
      setAddressSelected(true);
      setAddressSelectionError(false);
    }

  }, [currentAddressInfo]);

  const onSubmit = async (data: any) => {
    setHighlightError(0);
    if (!addressSelected && !manualAddressShown) {
      setAddressSelectionError(true);
      return;
    }

    if (residenceYearsText == "0" && residenceMonthsText == "0") {
      methods.setError("residenceYears", {
        type: "required",
        message: "Enter your time at this address.",
      });
      return;
    }

    if (data.streetType.trim() == "") {
      methods.setError("streetType", {
        type: "required",
        message: "Select your street type",
      });
      return;
    }

    if (data.country && data.country.trim().toLowerCase() == "canada") {
      //Verify postal code format
      //Canadian postal code regex: first letter (not D, F, I, O, Q, U, W, Z), digit, letter, optional space, digit, letter, digit
      const postalCodeRegex = /^[ABCEGHJ-NPRSTVXYabceghj-nprstvxy]\d[ABCEGHJ-NPRSTV-Zabceghj-nprstv-z][ ]?\d[ABCEGHJ-NPRSTV-Zabceghj-nprstv-z]\d$/;
      if (!postalCodeRegex.test(data.postalCode.trim())) {
        methods.setError("postalCode", {
          type: "pattern",
          message: " Please enter a valid postal code",
        });
        return;
      }
    }

    if (!manualAddressShown) {
      formatAddress();
    }

    //*set address complete here
    dispatch(setAddressComplete(true));
    dispatch(setResidentStatus(data.residentStatus));
    dispatch(setResidenceYears(+data.residenceYears));
    dispatch(setResidenceMonths(+data.residenceMonths));
    setIsSaving(true);
    await setHomeAddress(data);

    //If Current Address status is 'own', add (or update existing) a real estate asset automatically
    if (data.residentStatus.toLowerCase() == "own") {
      updateRealEstateAssets(data, true);
    } else {
      //Remove any existing real estate asset marked as current residence
      if (removeOwnRealEstateAsset) {
        updateRealEstateAssets(data, false);
      } else {
        proceedToNextPage(data);
      }
    }
  };

  const updateRealEstateAssets = async (data: any, includeOwnRealEstate: boolean) => {
    let nonRegistered: FinancialAssetDto[] = [];
    let registered: FinancialAssetDto[] = [];
    let realEstate: RealEstateDto[] = [];
    let vehicle: VehicleDto[] = [];
    let lifeInsurance: LifeInsuranceDto[] = [];
    let other: AssetDto[] = [];
    let downPayment: AssetDto[] = [];

    assets.forEach((asset: assetInfo) => {
      switch (asset.assetCategory) {
        case "nonregistered":
          nonRegistered.push(new FinancialAssetDto({
            fiName: asset.details.nonRegistered?.financialInstitution,
            fiNameOther: asset.details.nonRegistered?.otherFinancialInstitution,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType == "term_gic" ? "termgic" : asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "registered":
          registered.push(new FinancialAssetDto({
            fiName: asset.details.registered?.financialInstitution,
            fiNameOther: asset.details.registered?.otherFinancialInstitution,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "vehicle":
          vehicle.push(new VehicleDto({
            vehicleOwnershipType_Name: asset.details.vehicle?.ownership,
            year: asset.details.vehicle?.year ? +asset.details.vehicle.year : 0,
            make: asset.details.vehicle?.make,
            model: asset.details.vehicle?.model,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "realestate":
          if (!includeOwnRealEstate) {
            //skip adding this asset
            break;
          }
          let originalDate = undefined;
          if (asset?.details?.realEstate?.originalPurchaseDate && asset?.details?.realEstate?.originalPurchaseDate.trim() != "") {
            let dateComponents = asset.details.realEstate.originalPurchaseDate.split("/");
            originalDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
          }
          let soldDate = undefined;
          if (asset?.details?.realEstate?.soldDate && asset?.details?.realEstate?.soldDate.trim() != "") {
            let dateComponents = asset.details.realEstate.soldDate.split("/");
            soldDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
          }
          realEstate.push(new RealEstateDto({
            occupancyType_Name: asset.details.realEstate?.occupancyType,
            propertyType_Name: asset.details.realEstate?.propertyType,
            rentalComponent: asset.details.realEstate?.rentalComponent === "yes",
            numberOfProperties: asset.details.realEstate?.numOfProperties ? +asset.details.realEstate?.numOfProperties : 0,
            assetAddress_StreetNumber: asset.details.realEstate?.address?.streetNumber,
            assetAddress_StreetName: asset.details.realEstate?.address?.streetName,
            assetAddress_StreetType: asset.details.realEstate?.address?.streetType,
            assetAddress_StreetDirection: asset.details.realEstate?.address?.streetDirection,
            assetAddress_UnitType: asset.details.realEstate?.address?.unitType,
            assetAddress_UnitNumber: asset.details.realEstate?.address?.unitNumber,
            assetAddress_City: asset.details.realEstate?.address?.city,
            assetAddress_PostalCode: asset.details.realEstate?.address?.postalCode,
            assetAddress_Region: asset.details.realEstate?.address?.province,
            assetAddress_Country: asset.details.realEstate?.address?.country,
            origPurchasePrice: asset.details.realEstate?.originalPurchasePrice,
            origPurchaseDate: originalDate,
            lot: asset.details.realEstate?.lot,
            block: asset.details.realEstate?.block,
            plan: asset.details.realEstate?.plan,
            section: asset.details.realEstate?.section,
            futureUsageType_Name: asset.details.realEstate?.futureUsage,
            hasPropertyBeenSold: asset.details.realEstate?.hasPropertyBeenSold === "yes",
            soldDate: soldDate,
            rentalIncome: asset.details.realEstate?.rentalIncome,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "lifeinsurance":
          lifeInsurance.push(new LifeInsuranceDto({
            cashSurrenderValue: asset.details.lifeInsurance?.cashSurrenderValue,
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        case "other":
          other.push(new AssetDto({
            id: asset.id,
            assetCategoryType_Name: asset.assetCategory,
            assetType_Name: "other",
            origin_Name: "manual",
            value: asset.value,
            description: asset.description,
            downPaymentValue: asset.downPaymentValue,
            applicants: asset.applicants,
          }));
          break;
        default:
          downPayment.push(new AssetDto({
            id: asset.id,
            assetCategoryType_Name: "downpayment",
            assetType_Name: asset.assetType,
            origin_Name: "manual",
            value: 0,
            description: asset.description,
            downPaymentValue: asset.value,
            applicants: asset.applicants,
          }));
          break
      }
    });

    await client.apiAssetsPost(new DealAssetsDto({
      nonRegistered: nonRegistered,
      registered: registered,
      realEstate: realEstate,
      vehicle: vehicle,
      lifeInsurance: lifeInsurance,
      other: other,
      downPayment: downPayment,
    })).then(async (result: DealAssetsDto) => {
      // Handle the result if needed
      setIsSaving(false);
      if (result.nonRegistered && result.nonRegistered.length > 0) {
        result.nonRegistered.forEach((asset: FinancialAssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.nonRegistered && a.details.nonRegistered.financialInstitution === asset.fiName &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.registered && result.registered.length > 0) {
        result.registered.forEach((asset: FinancialAssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.registered && a.details.registered.financialInstitution === asset.fiName &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.realEstate && result.realEstate.length > 0) {
        let expenseList: PropertyExpenseDto[] = [];
        result.realEstate.forEach(async (asset: RealEstateDto) => {

          let existingExpense = expenses.find(exp => exp.assetId === asset.id);
          let existingAsset = assets.find(a => a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue);

          //Append Property Tax as Expense for Real Estate Asset
          let propertyExpenseDto = new PropertyExpenseDto({
            assetId: asset.id ? asset.id : 0,
            expenseCategoryType: "property",
            taxAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.propertyTax ? existingAsset.details.realEstate.propertyTax : 0,
            taxFrequency: "annually",
            condoFeesAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.maintenanceFee ? existingAsset.details.realEstate.maintenanceFee : 0,
            condoFeesFrequency: "monthly",
            utilityFeesAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.heatingCost ? existingAsset.details.realEstate.heatingCost : 0,
            utilityFeesFrequency: "monthly",
            id: existingExpense ? existingExpense.id : 0,
            applicants: realEstate[0].applicants
          });
          expenseList.push(propertyExpenseDto);

          assets.forEach((a: assetInfo) => {
            if (a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              let newDetails = { ...newAsset.details };
              newAsset.id = asset.id ? asset.id : 0;
              if (newAsset.details.realEstate?.address.streetNumber === homeAddress.address.streetNumber &&
                newAsset.details.realEstate?.address.streetName === homeAddress.address.streetName &&
                newAsset.details.realEstate?.address.streetType === homeAddress.address.streetType &&
                newAsset.details.realEstate?.address.city === homeAddress.address.city) {
                let realEstateDetails = { ...newDetails.realEstate };
                newDetails.realEstate = {
                  occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                  propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                  rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                  numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                  ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                  address: {
                    displayId: newAsset.displayId,
                    streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                    streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                    streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                    streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                    unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                    unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                    city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                    province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                    postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                    country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                  },
                  originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                  originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                  originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                  propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                  propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                  heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                  heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                  maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                  maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                  lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                  block: realEstateDetails.block ? realEstateDetails.block : "",
                  plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                  section: realEstateDetails.section ? realEstateDetails.section : "",
                  futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                  hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                  soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                  rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                  rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                  manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                  isCurrentResidence: true
                }
              }
              newAsset.details = newDetails;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
        await client.apiExpensesPost(new DealExpensesDto({
          property: expenseList,
        })).then((expenseResult) => {
          if (expenseResult.property && expenseResult.property.length > 0) {
            expenseResult.property.forEach((expense: PropertyExpenseDto) => {
              let existingExpense = expenses.find(exp => exp.id === expense.id);
              if (existingExpense == undefined) {
                let newExpense: expenseInfo = {
                  displayId: (expenses.length + 1).toString(),
                  id: expense.id ? expense.id : 0,
                  assetId: expense.assetId ? expense.assetId : 0,
                  expenseCategory: "property",
                  propertyTax: expense.taxAmount ? expense.taxAmount : 0,
                  heatingCost: expense.utilityFeesAmount ? expense.utilityFeesAmount : 0,
                  maintenanceFee: expense.condoFeesAmount ? expense.condoFeesAmount : 0,
                  ownershipPercentage: expense.ownershipPercentage ? expense.ownershipPercentage : 0,
                  applicants: expense.applicants ? expense.applicants : [],
                };
                dispatch(addExpense(newExpense));
              } else {
                let updateExpense = { ...existingExpense };
                updateExpense.propertyTax = expense.taxAmount ? expense.taxAmount : 0;
                updateExpense.heatingCost = expense.utilityFeesAmount ? expense.utilityFeesAmount : 0;
                updateExpense.maintenanceFee = expense.condoFeesAmount ? expense.condoFeesAmount : 0;
                dispatch(setExpenseInfo(updateExpense));
              }
            });
            //Remove expenses that are no longer linked to any assets
            let removableExpenses: number[] = [];
            expenses.forEach(exp => {
              let validExpense = false;
              expenseResult.property?.forEach((expense: PropertyExpenseDto) => {
                if (exp.id === expense.id) {
                  validExpense = true;
                }
              });
              if (!validExpense) {
                removableExpenses.push(exp.id);
              }
            });
            removableExpenses.forEach(id => {
              dispatch(removeExpense(id));
            });
          }

        }).catch((error) => {
          console.log("Error saving property expense for asset");
        });
      }
      if (result.vehicle && result.vehicle.length > 0) {
        result.vehicle.forEach((asset: VehicleDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.vehicle && a.details.vehicle.ownership === asset.vehicleOwnershipType_Name && a.details.vehicle.make === asset.make &&
              a.details.vehicle.model === asset.model && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.lifeInsurance && result.lifeInsurance.length > 0) {
        result.lifeInsurance.forEach((asset: LifeInsuranceDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.details.lifeInsurance && a.details.lifeInsurance.cashSurrenderValue === asset.cashSurrenderValue &&
              a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.downPayment && result.downPayment.length > 0) {
        result.downPayment.forEach((asset: AssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.assetType === asset.assetType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      if (result.other && result.other.length > 0) {
        result.other.forEach((asset: AssetDto) => {
          assets.forEach((a: assetInfo) => {
            if (a.assetCategory === asset.assetCategoryType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
              let newAsset = { ...a };
              newAsset.id = asset.id ? asset.id : 0;
              dispatch(setAssetInfo(newAsset));
            }
          });
        });
      }
      proceedToNextPage(data);
    }).catch((error) => {
      proceedToNextPage(data);
    });
  }

  const updateRealEstateAddress = () => {
    assets.forEach((asset) => {
      if (asset.assetCategory == "realestate" && asset.details?.realEstate?.isCurrentResidence) {
        //Update address if it was edited by user
        let newAssetInfo: assetInfo = { ...asset }
        let newDetails = { ...newAssetInfo.details };
        let realEstateDetails = { ...newAssetInfo.details.realEstate };
        realEstateDetails.address = {
          displayId: asset.displayId,
          streetNumber: methods.getValues("streetNumber"),
          streetName: methods.getValues("streetName"),
          streetType: methods.getValues("streetType"),
          streetDirection: methods.getValues("streetDirection"),
          city: methods.getValues("cityName"),
          province: methods.getValues("provinceName"),
          country: methods.getValues("country"),
          postalCode: methods.getValues("postalCode"),
          unitType: methods.getValues("unitType"),
          unitNumber: methods.getValues("unitNumber")
        }
        newDetails.realEstate = {
          occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
          propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
          rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
          numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
          ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
          address: {
            displayId: asset.displayId,
            streetNumber: methods.getValues("streetNumber") ? methods.getValues("streetNumber") : "",
            streetName: methods.getValues("streetName") ? methods.getValues("streetName") : "",
            streetType: methods.getValues("streetType") ? methods.getValues("streetType") : "",
            streetDirection: methods.getValues("streetDirection") ? methods.getValues("streetDirection") : "",
            unitType: methods.getValues("unitType") ? methods.getValues("unitType") : "",
            unitNumber: methods.getValues("unitNumber") ? methods.getValues("unitNumber") : "",
            city: methods.getValues("cityName") ? methods.getValues("cityName") : "",
            province: methods.getValues("provinceName") ? methods.getValues("provinceName") : "",
            postalCode: methods.getValues("postalCode") ? methods.getValues("postalCode") : "",
            country: methods.getValues("country") ? methods.getValues("country") : ""
          },
          originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
          originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
          originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
          propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
          propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
          heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
          heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
          maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
          maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
          lot: realEstateDetails.lot ? realEstateDetails.lot : "",
          block: realEstateDetails.block ? realEstateDetails.block : "",
          plan: realEstateDetails.plan ? realEstateDetails.plan : "",
          section: realEstateDetails.section ? realEstateDetails.section : "",
          futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
          hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
          soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
          rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
          rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
          manualAddressEntered: false,
          isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
        }
        newAssetInfo.details = newDetails;
        dispatch(setAssetInfo(newAssetInfo));
      }
    });
  }

  const proceedToNextPage = (data: any) => {
    setIsSaving(false);
    let totalOccupancyTime = (+data.residenceYears * 12) + +data.residenceMonths;
    setClickInsightApplicationProgress('Build your profile - Where do you live?');
    if (totalOccupancyTime < 36) {
      dispatch(setApplicationProgress(9));
      navigate("/single/previousaddress");
    } else {
      dispatch(setApplicationProgress(10));
      dispatch(clearPreviousAddressState());
      navigate("/single/employment");
    }
  }

  const focusError = () => {
    if (!manualAddressShown) {
      toggleManualAddress();

      if (methods.getValues("streetNumber") == "") {
        methods.setError("streetNumber", {
          type: "required",
          message: "Enter your street number",
        });
      }
      if (methods.getValues("streetName") == "") {
        methods.setError("streetName", {
          type: "required",
          message: "Enter your street name",
        });
      }
      if (methods.getValues("streetType") == "") {
        methods.setError("streetType", {
          type: "required",
          message: "Select your street type",
        });
      }
      if (methods.getValues("cityName") == "") {
        methods.setError("cityName", {
          type: "required",
          message: "Enter your city name.",
        });
      }
      if (methods.getValues("provinceName") == "") {
        methods.setError("provinceName", {
          type: "required",
          message: "Select your province name",
        });
      }
      if (methods.getValues("postalCode") == "") {
        methods.setError("postalCode", {
          type: "required",
          message: "Enter your postal code",
        });
      }
      if (methods.getValues("country") == "") {
        methods.setError("country", {
          type: "required",
          message: "Select your country name",
        });
      }
    }

    if (methods.formState?.errors?.streetNumber?.message && methods.formState?.errors?.streetNumber?.message != "") {
      setFocusErrorType(1);
      setHighlightError(1);
      try {
        document.getElementById("streetNumber")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.unitNumber?.message && methods.formState?.errors?.unitNumber?.message != "") {
      setFocusErrorType(2);
      setHighlightError(2);
      setHighlightError(2);
      try {
        document.getElementById("unitNumber")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.streetName?.message && methods.formState?.errors?.streetName?.message != "") {
      setFocusErrorType(3);
      setHighlightError(3);
      setHighlightError(3);
      try {
        document.getElementById("street-address")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.streetType?.message && methods.formState?.errors?.streetType?.message != "") {
      setFocusErrorType(4);
      setHighlightError(4);
      setHighlightError(4);
      try {
        document.getElementById("streetType")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.cityName?.message && methods.formState?.errors?.cityName?.message != "") {
      setFocusErrorType(5);
      setHighlightError(5);
      setHighlightError(5);
      try {
        document.getElementById("cityName")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.provinceName?.message && methods.formState?.errors?.provinceName?.message != "") {
      setFocusErrorType(6);
      setHighlightError(6);
      setHighlightError(6);
      try {
        document.getElementById("province")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.postalCode?.message && methods.formState?.errors?.postalCode?.message != "") {
      setFocusErrorType(7);
      setHighlightError(7);
      setHighlightError(7);
      try {
        document.getElementById("postcode")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.country?.message && methods.formState?.errors?.country?.message != "") {
      setFocusErrorType(8);
      setHighlightError(8);
      setHighlightError(8);
      try {
        document.getElementById("country")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.residentStatus?.message && methods.formState?.errors?.residentStatus?.message != "") {
      setFocusErrorType(9);
      setHighlightError(9);
      setHighlightError(9);
      try {
        document.getElementById("residentStatus")?.focus();
      } catch { }
    } else if ((methods.formState?.errors?.residenceYears?.message && methods.formState?.errors?.residenceYears?.message != "") ||
      (methods.formState?.errors?.residenceMonths?.message && methods.formState?.errors?.residenceMonths?.message != "")) {
      setFocusErrorType(10);
      setHighlightError(10);
      setHighlightError(10);
      try {
        document.getElementById("residentYears")?.focus();
      } catch { }
    }

    const timeout = setTimeout(() => {
      setFocusErrorType(0);
    }, 1000)
    return () => clearTimeout(timeout)
  }

  const getAddressData = (id: any, type: any, val: any) => {
    let newAddressData = { ...currentAddressInfo }

    switch (type) {
      case "streetNumber":
        newAddressData.streetNumber = val;
        break;
      case "streetName":
        newAddressData.streetName = val;
        break;
      case "streetType":
        newAddressData.streetType = val;
        break;
      case "streetDirection":
        newAddressData.streetDirection = val;
        break;
      case "unitNumber":
        newAddressData.unitNumber = val;
        break;
      case "unitType":
        newAddressData.unitType = val;
        break;
      case "cityName":
        newAddressData.city = val;
        break;
      case "province":
        newAddressData.province = val;
        break;
      case "postalCode":
        newAddressData.postalCode = val;
        break;
      case "country":
        newAddressData.country = val;
        //Reset province name if country is changed
        newAddressData.province = "";
        methods.setValue("provinceName", "");
        break;
    }
    setCurrentAddressInfo(newAddressData);
    if (application.applicationCategory == applicationCategory.Buy) {
      updateRealEstateAddress();
    }
  }

  return (
    <>
      <SlideTransition>
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
          <div
            className={style.addHeading}
            role="region"
            aria-label="Address Header"
          >
            <div style={{ width: '-webkit-fill-available' }}>
              <h1 aria-label="Address Header">Where do you live?</h1>
              <p className="sub-heading">Add your address to your profile.</p>
            </div>
            {displayError &&
              <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                <img src="assets/error-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">
                  Please edit the highlighted fields before continuing
                  <span className="underline cursor-pointer ml-5" onClick={focusError} style={{ color: "var(--error-red)" }}>Go to the first error</span>
                </span>
              </div>
            }
            <div className="flex flex-row mt-5">
              <FormProvider {...methods}>
                <form
                  autoComplete="off"
                  onSubmit={methods.handleSubmit(onSubmit, onError)}
                >
                  <div className="col-span-1">
                    <div>
                      <label htmlFor="search">Search for your address</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/magnifying-glass-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.autoAddress
                                ? "hidden"
                                : "",
                              "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="search"
                            height={5}
                            width={5}
                          />
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.autoAddress
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          autoComplete="nope"
                          className={clsx(
                            methods.formState?.errors?.autoAddress?.message
                              ? "error"
                              : "",
                            "mt-2"
                          )}
                          id="search"
                          type="text"
                          placeholder="Start typing address"
                          {...methods.register("autoAddress")}
                        />

                      </div>
                      {addressSelectionError && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          You must select an address from the list of options
                        </span>
                      )}
                      <div
                        className={clsx("ml-2 mt-2", showAddress ? "" : "hidden")}
                      >
                        <p id="formatted-address"></p>
                        <span id="span-street-address"></span>
                        <br />
                        <span id="span-city"></span>&nbsp;
                        <span id="span-state"></span>&nbsp;
                        <span id="span-postcode"></span>
                        <br />
                        <span id="span-country"></span>
                      </div>
                      <div
                        className="flex relative mt-3"
                      >
                        <p className="active underline cursor-pointer" onClick={() => toggleManualAddress()}>{manualAddressShown ? "Hide address fields" : "Enter address manually"}</p>
                      </div>
                    </div>

                    <AnimatePresence>
                      {manualAddressShown &&
                        <motion.div
                          key={0}
                          initial={{ y: -100, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ opacity: 0 }}
                        ><ManualAddress passAddressData={getAddressData} addressData={currentAddressInfo} manualAddressToggled={manualAddressShown}
                          focusErrorType={focusErrorType} highlightError={highlightError} primaryAddress={true} /></motion.div>
                      }
                    </AnimatePresence>
                  </div>
                  {/* <div className="mt-5 flex flex-col-reverse">
                  <label></label>
                  <input
                    id="mailingIsSame"
                    type="checkbox"
                    value={isMailAddressChecked ? "true" : "false"}
                    className="accent"
                    {...methods.register("isMailingAddressSame", {
                      onChange: toggleMailingAddress,
                    })}
                    defaultChecked
                  />
                  <label htmlFor="mailingIsSame">
                    <p className="sub-heading ml-7 -mt-5">
                      Mailing address is the same as home address
                    </p>
                  </label>
                </div>
                <div
                  className={clsx(
                    isMailAddressChecked ? "hidden" : "",
                    "flex relative"
                  )}
                >
                  <div className="mt-5">
                    <MailingAddress mailingAddressSelectionError={mailingAddressSelectionError} mailingAddressSelected={setMailingAddressHandler}
                      manualAddressToggled={toggleManualMailingAddress} />
                  </div>
                </div> */}
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div className={focusErrorType == 9 ? "animate__animated animate__pulse" : ""}>
                      <label htmlFor="residentStatus">Living Situation</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.residentStatus ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[420px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <select
                          id="residentStatus"
                          {...methods.register("residentStatus", {
                            required: "Select an option",
                            onChange: residentStatusChangeHandler
                          })}
                          onChange={residentStatusChangeHandler}
                          value={residentStatusText}
                          className={clsx(
                            "", "mt-2 pt-2.5", highlightError == 9 ? style.focusError : ""
                          )}
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="own">Own</option>
                          <option value="rent">Rent</option>
                          <option value="liveswithparents">Live with Parents</option>
                          <option value="board">Board</option>
                        </select>
                      </div>
                      {methods.formState?.errors?.residentStatus?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.residentStatus.message}
                        </span>
                      )}
                    </div>
                    <div className={clsx("md:ml-4", focusErrorType == 10 ? "animate__animated animate__pulse" : "")}>
                      <label htmlFor="title">How long have you lived here?</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.residenceYears ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[70px] xs:max-md:ml-[370px] md:ml-[120px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <div className="flex">
                          <input
                            className={clsx(
                              "mt-2", style.residentPeriod
                            )}
                            type="number"
                            min={0}
                            placeholder="year(s)"
                            max={99}
                            id="residentLengthYears"
                            value={residenceYearsText}
                            {...methods.register("residenceYears", {
                              required: "Enter your time at this address.",
                              onChange: residenceYearsChangeHandler
                            })}
                            onChange={residenceYearsChangeHandler}
                            onFocus={(e) => e.target.select()}
                          />
                          {residenceYearsText != "" &&
                            <span style={{ position: "absolute", top: "19px", left: residenceYearsText.length > 1 ? "28px" : "22px" }}>year{+residenceYearsText != 1 ? "s" : ""}</span>
                          }
                        </div>
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.residenceMonths ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[120px] xs:max-md:ml-[370px] md:ml-[140px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <div className="flex">
                          <input
                            className={clsx(
                              "mt-2 ml-2", style.residentPeriod
                            )}
                            type="number"
                            min={0}
                            max={11}
                            placeholder="month(s)"
                            id="residentLengthMonths"
                            value={residenceMonthsText}
                            {...methods.register("residenceMonths", {
                              required: "Enter the number of months you lived in your current residence.",
                              onChange: residenceMonthsChangeHandler
                            })}
                            onChange={residenceMonthsChangeHandler}
                            onFocus={(e) => e.target.select()}
                          />
                          {residenceMonthsText != "" &&
                            <span style={{ position: "absolute", top: "19px", left: residenceMonthsText.length > 1 ? "201px" : "195px" }}>month{+residenceMonthsText != 1 ? "s" : ""}</span>
                          }
                        </div>
                      </div>
                      {methods.formState?.errors?.residenceYears?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.residenceYears.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex max-sm:hidden">
                    <div className="mb-5 mr-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-169 sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                  </div>
                  <div className="sm:hidden">
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                    <div className="mb-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-full sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </SlideTransition>
    </>
  );
}
